import { setActiveDrawableGroupFromID } from '../../../../actions/drawable'
import { DRAWABLE_TYPES, DRAWING_TYPES, PAPER_ELEMENT_TYPE } from '../../../../shared/constants/drawable-types'
import store from '../../../../stores'
import {
    bulkUpdateOpeningLocationCoordinatesAction,
    BulkUpdateOpeningLocationCoordinatesPayload,
} from '../../../sagas/2D/bulkUpdateOpeningLocationCoordinates'
import { closeMaterialFormAction } from '../../../sagas/effects/handleCloseMaterialForm'
import { editingActive, selectedPaperItem, updateSelectedCoordinates } from '../../../slices/2D'
import {
    MaterialInteractionState,
    selectAreBaseHomeMaterialsReadOnly,
    selectAreMaterialInteractionsBlocked,
    selectIsMaterialGroupActivationBlocked,
} from '../../../slices/masterSetPlan'
import { setCutoutItem } from '../../../slices/tools'
import { DeselectContext, IMUP2DCoordinatesToUpdate } from '../../../types'

const disabledSelectionBindings = new WeakMap<object, { onSelect: () => void; isMultiSelectable: boolean }>()

export function getDrawableSelectionState(state: MaterialInteractionState) {
    return {
        areInteractionsBlocked: selectAreMaterialInteractionsBlocked(state),
        isGroupActivationBlocked: selectIsMaterialGroupActivationBlocked(state),
        areBaseMaterialsReadOnly: selectAreBaseHomeMaterialsReadOnly(state),
        optionChunkIds: [
            ...state.IMUP['2D'].currentOverlayChunkIds,
            ...state.IMUP['2D'].currentEstimatedOptionOverlayChunkIds,
        ],
    }
}

export function updateDrawableSelection(drawable: paper.Item, selection: ReturnType<typeof getDrawableSelectionState>) {
    const { data } = drawable
    const isBlocked =
        selection.areInteractionsBlocked ||
        (selection.isGroupActivationBlocked && !data.createdDuringConflictResolution) ||
        (selection.areBaseMaterialsReadOnly && !selection.optionChunkIds.includes(data.document_chunk_id))

    if (isBlocked) {
        if (data.onSelect && !disabledSelectionBindings.has(data)) {
            disabledSelectionBindings.set(data, { onSelect: data.onSelect, isMultiSelectable: data.isMultiSelectable })
        }
        delete data.onSelect
        data.isMultiSelectable = false
    } else {
        const bindings = disabledSelectionBindings.get(data)

        if (bindings) {
            Object.assign(data, bindings)
            disabledSelectionBindings.delete(data)
        }
    }
}

function save2DDrawable(): (coordinatesToUpdate: IMUP2DCoordinatesToUpdate) => void {
    return function saveDrawable(coordinatesToUpdate) {
        store.dispatch(updateSelectedCoordinates(coordinatesToUpdate))
    }
}

function multiSelectSave2DDrawable(): (payload: BulkUpdateOpeningLocationCoordinatesPayload) => void {
    return function saveDrawable(coordinatesToUpdate) {
        store.dispatch(bulkUpdateOpeningLocationCoordinatesAction(coordinatesToUpdate))
    }
}

function select2DDrawable(): () => void {
    return function selectDrawable(this: paper.Item) {
        const state = store.getState()

        if (selectAreMaterialInteractionsBlocked(state)) return

        if (selectIsMaterialGroupActivationBlocked(state)) {
            store.dispatch(
                selectedPaperItem({
                    selectedItem: this.id,
                    activeDrawableId: this.data.drawable_id,
                    activeOpeningLocationId: this.data.opening_location_id,
                    openingGroupId: this.data.opening_group_id,
                })
            )
            store.dispatch(setCutoutItem(this.data.shapeType === DRAWING_TYPES.AREA ? this.id : null))
            store.dispatch(editingActive(false))

            return
        }

        store.dispatch(
            setActiveDrawableGroupFromID(
                this.id,
                this.data.drawable_id,
                this.data.opening_group_id,
                this.data.opening_location_id
            )
        )
        store.dispatch(editingActive(false))
    }
}

function deselect2DDrawable(): () => void {
    return function deselectDrawable(this: paper.Item, context?: DeselectContext) {
        store.dispatch(
            selectedPaperItem({
                selectedItem: null,
                activeDrawableId: null,
                activeOpeningLocationId: null,
                openingGroupId: null,
            })
        )

        if (context?.ctrlKey) {
            store.dispatch(closeMaterialFormAction())
        }
    }
}

function cutout2DDrawable(): (coordinatesToUpdate: IMUP2DCoordinatesToUpdate) => void {
    return save2DDrawable()
}

export function determineIfMultiSelectable(drawable: paper.Item) {
    if (drawable.data.drawing_type === DRAWABLE_TYPES.WALL) {
        return drawable.data.shapeType !== DRAWING_TYPES.POINT
    }

    if (
        drawable.data.drawing_type === DRAWABLE_TYPES.FLOOR_SYSTEM ||
        drawable.data.drawing_type === DRAWABLE_TYPES.FLOOR_SYSTEMS ||
        drawable.data.drawing_type === DRAWABLE_TYPES.ROOF_SYSTEMS ||
        drawable.data.drawing_type === DRAWABLE_TYPES.ROOF_SYSTEM
    ) {
        return drawable.data.shapeType !== DRAWING_TYPES.SECTION
    }

    return true
}

export default function addSelectFunctionalityToDrawable(drawable: paper.Item): void {
    drawable.data.type = PAPER_ELEMENT_TYPE.DRAWABLE
    drawable.data.isMultiSelectable = determineIfMultiSelectable(drawable)
    drawable.data.onSave = save2DDrawable().bind(drawable)
    drawable.data.onSelect = select2DDrawable().bind(drawable)
    drawable.data.onDeselect = deselect2DDrawable()
    drawable.data.onMultiSelectSave = multiSelectSave2DDrawable()
    drawable.data.onCutout = cutout2DDrawable()
    disabledSelectionBindings.delete(drawable.data)
    updateDrawableSelection(drawable, getDrawableSelectionState(store.getState()))
}
